<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in filteredRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-9">
                <p>{{ prefix }}</p>
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { computed, defineComponent, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useColors } from 'vuestic-ui'

import { INavigationRoute, Nav } from '../../router'
import { usePermissionStore } from '../../services/permission/permission-store'
import { roleService } from '../../services/role/roleService'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    prefix: { type: String, default: '•' },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const permissionStore = usePermissionStore()

    const value = ref<boolean[]>([])
    const navigationRoutes = inject<Nav>('nav-routes')!
    const filteredRoutes = ref<INavigationRoute[]>([])
    const isAdmin = ref(false)
    const isManager = ref(false)

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route?.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!route) return false

      if (!section.children) {
        return route.path?.endsWith(`${section.name}`) ?? false
      }

      return section.children.some(({ name }) => route.path?.endsWith(`${name}`) ?? false)
    }

    const setActiveExpand = () =>
      (value.value = filteredRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // Recursively filter routes based on permissions
    const filterRouteRecursive = (
      route: INavigationRoute,
      permissionMap: Map<string, boolean>,
    ): INavigationRoute | null => {
      if (route.requiredPermission && !permissionMap.get(route.requiredPermission)) {
        return null
      }

      if (route.children) {
        const filteredChildren = route.children
          .map((child) => filterRouteRecursive(child, permissionMap))
          .filter((child): child is INavigationRoute => child !== null)

        // Hide parent if all children are filtered out
        if (filteredChildren.length === 0) {
          return null
        }

        return { ...route, children: filteredChildren }
      }

      return route
    }

    // Filter routes based on permissions
    const filterRoutes = async () => {
      isAdmin.value = await permissionStore.isAdmin()

      // Check if user has Manager role
      try {
        isManager.value = await roleService.hasManagerRole()
      } catch (error) {
        console.error('Failed to check manager role:', error)
        isManager.value = false
      }

      // If admin, show all routes
      if (isAdmin.value) {
        filteredRoutes.value = navigationRoutes.routes
        return
      }

      const permissionsNeeded = new Set<string>()
      const collectPermissions = (route: INavigationRoute) => {
        if (route.requiredPermission) {
          permissionsNeeded.add(route.requiredPermission)
        }
        if (route.children) {
          route.children.forEach((child) => collectPermissions(child))
        }
      }

      navigationRoutes.routes.forEach((route) => collectPermissions(route))
      const permissionsArray = Array.from(permissionsNeeded)
      const permissions = await permissionStore.checkPermissions(permissionsArray)
      const permissionMap = new Map<string, boolean>()
      permissionsArray.forEach((perm, index) => {
        permissionMap.set(perm, permissions[index])
      })

      filteredRoutes.value = navigationRoutes.routes
        .map((route) => filterRouteRecursive(route, permissionMap))
        .filter((route): route is INavigationRoute => route !== null)
    }

    onMounted(async () => {
      await filterRoutes()
    })

    watch(() => route?.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
